import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments';
import * as fromRootStore from '@store/index';
import { selectVgClientPoid } from '@store/selectors/client-accounts/client-accounts.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import { combineLatest, mergeMap, Observable, Subscription } from 'rxjs';

import { CookiesService } from '../../cookies/cookies';
import { WindowRef } from '../../window-ref/window-ref';
import { SKIP_URL_MUTATION } from '../context-tokens';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  private $window: any;
  subs: Subscription = new Subscription();

  constructor(
    private $cookies: CookiesService,
    private windowRef: WindowRef,
    private globalStore: Store<fromRootStore.State>,
  ) {
    this.$window = windowRef.nativeWindow;
  }

  /**
   * Local Development:
   * We need to prefix the http request URL with an absolute domain for local development
   * because the URLs in the application are all relative and get routed to middletier
   * via NGINX
   *
   * Internal Envs:
   * We need to prefix the http request URL with an absolute domain for internal environments,
   * all internal users will be on the vanguard network
   *
   * External Envs:
   * All URLs are relative / don't contain domain. The routing of traffic happens at the NGINX level,
   * and is differentiated by /api path prefix.
   */
  private getURL(url: string): string {
    switch (environment.name) {
      // Local development
      case 'local.external':
      case 'local.internal':
        return `https://localhost.vanguard.com:8081/${url}`;
      case 'local.test.external':
        return `https://cwp-change-of-ownership-angular.ecswa.rett.c1.vanguard.com/cwp-change-of-ownership-angular/${url}`;
      case 'local.test-2.external':
        return `https://cwp-change-of-ownership-angular.ecswa.rett.c1.vanguard.com/cwp-change-of-ownership-angular-2/${url}`;
      case 'local.test-3.external':
        return `https://cwp-change-of-ownership-angular.ecswa.rett.c1.vanguard.com/cwp-change-of-ownership-angular-3/${url}`;
      case 'local.test-4.external':
        return `https://cwp-change-of-ownership-angular.ecswa.rett.c1.vanguard.com/cwp-change-of-ownership-angular-4/${url}`;
      // Internal environments
      case 'eng.internal':
      // eng evironment points to SAT for testing purposes
      case 'test.internal':
        return `https://cwp-change-of-ownership-angular.ecswai.rett.c1.vanguard.com/cwp-change-of-ownership-angular/${url}`;
      case 'prod.internal':
        return `https://cwp-change-of-ownership-angular.ecswai.retp.c1.vanguard.com/cwp-change-of-ownership-angular/${url}`;
      // External environments
      // eng evironment points to SAT for testing purposes
      case 'eng.external':
        return `https://satpersonal1.vanguard.com/cwp-change-of-ownership-angular/api/${url}`;
      case 'test.external':
      case 'test-2.external':
      case 'test-3.external':
      case 'test-4.external':
      case 'prod.external':
        return `api/${url}`;
      // e2e environment - cy.intercepts don't have /api
      case 'e2e':
      case 'e2e-templates':
        return url;
      default:
        // Exhaustive check
        const n: never = environment.name;
        return url;
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    /**
     * The XMLHttpRequest.withCredentials property is a Boolean that indicates
     * whether or not cross-site Access-Control requests should be made using
     * credentials such as cookies, authorization headers or TLS client certificates.
     * Setting withCredentials has no effect on same-site requests.
     */
    const url =
      request.url.startsWith('https') || request.context.get(SKIP_URL_MUTATION)
        ? request.url
        : this.getURL(request.url);
    request = request.clone({ withCredentials: true, url });

    return combineLatest([
      this.globalStore.select(flowTypeSelection),
      this.globalStore.select(selectVgClientPoid),
    ]).pipe(
      mergeMap(([flowType, poid]) => {
        const headers: { [key: string]: string } = {};
        // Pass flowType to all requests
        if (flowType) {
          headers['Flow-Type'] = flowType;
        }
        // Pass poid to all requests (helps with public API calls that don't have poid attached to headers via Siteminder)
        if (poid) {
          headers.poid = poid;
        }

        /**
         * When the XSRF token is not present on request headers on any POST call,
         * read value from cookie and manually set header.  Most scenarios where
         * header is missing is on CORS requests.
         */
        const xsrfToken = this.$cookies.get('XSRF-TOKEN');
        if (request.method === 'POST' && !request.headers.has('X-XSRF-TOKEN') && xsrfToken) {
          headers['X-XSRF-TOKEN'] = xsrfToken;
        }
        request = request.clone({
          setHeaders: headers,
        });

        return next.handle(request);
      }),
    );
  }
}
